/**
 * RelinkProject — the project ↔ client / partner re-link editor (item 1).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  2026-05-21 (Anil) — owner / Khilendra data-correction tool.
 * ─────────────────────────────────────────────────────────────────────────
 *  The 595 imported projects can be linked to the WRONG client, or carry a
 *  wrong / missing partner. A team member must be able to VERIFY and CORRECT
 *  that link. This editor lets them re-assign a project's `clientId` and
 *  `partnerId` (partner nullable = "direct engagement").
 *
 *  It is deliberately a SHARED component so it is discoverable from every
 *  surface that shows a project:
 *    · the project list           (AllProjects.tsx — "Re-link" per row)
 *    · the Client Detail flow     (ClientDetail.tsx — per project row)
 *    · the Partner Detail flow    (PartnerDetail.tsx — per project row)
 *
 *  Client choices are folded from the live project stream (the IA-fix
 *  `clientRowsFromProjects` aggregator) — the same data the Clients screen
 *  shows; partner choices come from the `partners/` master. Picking a
 *  client also captures its `clientNo` / `clientName` so the display cache
 *  on the project stays in sync with the `clientId` it now points at.
 */
import { useEffect, useState } from 'react';
import {
  listenPartners,
  relinkProject,
  projectLabel,
  type ClientRow,
  type LivePartner,
} from '../lib/projectStore';
import { ClientPicker } from './ClientPicker';
import { Btn3 } from './ui';

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '12px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

/** The minimal project shape the editor needs to show + re-link. */
export interface RelinkTarget {
  pid: string;
  vp: string;
  /** The project's own name (2026-05-22) — optional so a caller that has
   *  no name handy still type-checks; the panel shows the VP fallback. */
  projectName?: string;
  clientId: string;
  clientNo: string;
  clientName: string;
  partnerId: string | null;
}

/**
 * The inline re-link panel. Render it conditionally below a project row
 * (`open` is owned by the parent so it behaves like the existing per-row
 * Edit panels). `onDone` reports a toast message; `onClose` collapses it.
 */
export function RelinkProjectPanel({
  project,
  actorName,
  onDone,
  onClose,
}: {
  project: RelinkTarget;
  actorName: string;
  onDone: (msg: string) => void;
  onClose: () => void;
}) {
  const [partners, setPartners] = useState<LivePartner[]>([]);
  // The currently-selected client. Starts as the project's CURRENT client
  // (built from the project's display cache); the ClientPicker swaps in a
  // full ClientRow when the team member picks a different existing client.
  const [chosenClient, setChosenClient] = useState<ClientRow>({
    clientId: project.clientId,
    clientNo: project.clientNo,
    name: project.clientName,
    projectCount: 0,
    archivedCount: 0,
    totalValueINR: 0,
  });
  const [partnerId, setPartnerId] = useState(project.partnerId ?? '');
  const [busy, setBusy] = useState(false);

  // The partner directory — only un-archived partners are pickable.
  useEffect(() => {
    const unsub = listenPartners(
      (rows) => setPartners(rows.filter((p) => !p.archived)),
      {
        /* the picker shows no partners if this fails */
      },
    );
    return unsub;
  }, []);

  const dirty =
    chosenClient.clientId !== project.clientId ||
    (partnerId || null) !== (project.partnerId || null);

  async function save() {
    if (busy || !chosenClient.clientId) return;
    setBusy(true);
    try {
      await relinkProject(
        project.pid,
        {
          clientId: chosenClient.clientId,
          clientNo: chosenClient.clientNo,
          clientName: chosenClient.name,
          partnerId: partnerId || null,
        },
        actorName,
      );
      onDone(
        `${project.vp} re-linked to ${chosenClient.clientNo} ` +
          `(${chosenClient.name}).`,
      );
      onClose();
    } catch (e) {
      onDone(`Re-link failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 14,
        borderTop: '1px solid var(--rule-strong)',
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          color: 'var(--ink-soft)',
          marginBottom: 4,
        }}
      >
        Verify and correct which client and partner{' '}
        <b style={{ color: 'var(--ink)' }}>
          {projectLabel(project.projectName, project.vp)}
        </b>{' '}
        ({project.vp}) is linked to. The client number is the project's
        permanent owner.
      </div>

      <Lbl>Client (project owner)</Lbl>
      <ClientPicker
        value={chosenClient.clientId}
        onPick={setChosenClient}
        ariaLabel="Re-link project to client"
        placeholder="Type a client name or number to re-link…"
      />
      <div
        style={{
          fontSize: 12,
          color: 'var(--ink-mute)',
          marginTop: 5,
        }}
      >
        Linked to{' '}
        <b style={{ color: 'var(--ink)' }}>
          {chosenClient.name || chosenClient.clientId}
        </b>{' '}
        ({chosenClient.clientNo})
      </div>

      <Lbl>Channel partner</Lbl>
      <select
        value={partnerId}
        onChange={(e) => setPartnerId(e.target.value)}
        aria-label="Re-link project to channel partner"
        style={inputStyle}
      >
        <option value="">No channel partner (direct AV engagement)</option>
        {partners.map((p) => (
          <option key={p.pid} value={p.pid}>
            {p.name || p.partnerNo} ({p.partnerNo})
          </option>
        ))}
      </select>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 14,
          alignItems: 'center',
        }}
      >
        <Btn3
          intent="grn"
          size="sm"
          onClick={save}
          disabled={busy || !dirty || !chosenClient.clientId}
        >
          {busy ? 'Re-linking…' : 'Save re-link'}
        </Btn3>
        <Btn3 intent="ghost" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Btn3>
        {!dirty && (
          <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            No change to save.
          </span>
        )}
      </div>
    </div>
  );
}
